import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Gift, Link2, Mail, MessageCircle } from "lucide-react";
import { claimShareDiscount, fetchShareDiscount, trackEvent } from "@/lib/api";

const SYM = { EUR: "€", USD: "$", GBP: "£" };

/** Share row + share nudge. One tap to X, LinkedIn, WhatsApp, email or
 *  clipboard; every click is tracked as a `share` event with its channel.
 *  When a share discount is live, a teaser sits above the buttons and the
 *  visitor can trade their email for a single-use code after sharing. */
export const ShareRow = ({ slug, domain, priceLabel, sessionId, accent = "#3ee8d3", onCodeIssued }) => {
  const [copied, setCopied] = useState(false);
  const [offer, setOffer] = useState(null);      // {eligible, amount, currency, days}
  const [shared, setShared] = useState(false);
  const [email, setEmail] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [code, setCode] = useState(null);        // {code, amount, currency, expires_at, new_total}
  const [codeCopied, setCodeCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let dead = false;
    fetchShareDiscount(slug)
      .then((d) => { if (!dead) setOffer(d); })
      .catch(() => {});
    return () => { dead = true; };
  }, [slug]);

  const url = typeof window !== "undefined" ? `${window.location.origin}/l/${domain || slug}` : "";
  const line = priceLabel ? `${domain} is for sale — ${priceLabel}.` : `${domain} is for sale.`;
  const sym = SYM[offer?.currency] || "";
  const rewardLabel = offer?.eligible ? `${sym}${Number(offer.amount).toLocaleString()} off` : "";

  const fire = useCallback((channel) => {
    setShared(true);
    return trackEvent(slug, { event: "share", channel, session_id: sessionId });
  }, [slug, sessionId]);

  const open = (channel, href) => {
    fire(channel);
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");
  };

  const copy = async () => {
    fire("copy");
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  const claim = async (e) => {
    e.preventDefault();
    setError("");
    setClaiming(true);
    try {
      const r = await claimShareDiscount(slug, { email, session_id: sessionId });
      setCode(r);
      onCodeIssued?.(r);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not issue a code — please retry.");
    } finally {
      setClaiming(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2200);
    } catch { /* clipboard blocked */ }
  };

  const targets = [
    {
      key: "x", label: "X", icon: <XIcon />,
      run: () => open("x", `https://x.com/intent/post?text=${encodeURIComponent(line)}&url=${encodeURIComponent(url)}`),
    },
    {
      key: "linkedin", label: "LinkedIn", icon: <LinkedInIcon />,
      run: () => open("linkedin", `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`),
    },
    {
      key: "whatsapp", label: "WhatsApp", icon: <MessageCircle size={16} strokeWidth={1.6} />,
      run: () => open("whatsapp", `https://wa.me/?text=${encodeURIComponent(`${line} ${url}`)}`),
    },
    {
      key: "email", label: "Email", icon: <Mail size={16} strokeWidth={1.6} />,
      run: () => {
        fire("email");
        window.location.href = `mailto:?subject=${encodeURIComponent(line)}&body=${encodeURIComponent(`${line}\n\n${url}`)}`;
      },
    },
    {
      key: "copy",
      label: copied ? "Copied" : "Copy link",
      icon: copied ? <Check size={16} strokeWidth={2} /> : <Copy size={16} strokeWidth={1.6} />,
      run: copy,
    },
  ];

  return (
    <section
      className="cd-container"
      aria-label="Share this domain"
      data-testid="lander-share-row"
      style={{ marginTop: 48 }}
    >
      <div
        style={{
          padding: "18px 22px",
          border: "1px solid var(--cd-gray-200)",
          borderRadius: 14,
          background: "linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%)",
        }}
      >
        {offer?.eligible && !code && (
          <div
            data-testid="share-nudge-teaser"
            style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
              paddingBottom: 14, borderBottom: "1px solid var(--cd-gray-200)",
            }}
          >
            <span
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28, borderRadius: 999,
                background: `${accent}22`, color: "var(--cd-navy-950)", flexShrink: 0,
              }}
            >
              <Gift size={15} strokeWidth={1.8} />
            </span>
            <span style={{ fontSize: 14, color: "var(--cd-navy-950)", fontWeight: 600 }}>
              Share {domain} and get <strong>{rewardLabel}</strong>
              <span style={{ color: "var(--cd-gray-600)", fontWeight: 500 }}>
                {" "}— we'll email you a single-use code, valid {offer.days} days.
              </span>
            </span>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: "auto" }}>
            <Link2 size={16} strokeWidth={1.6} style={{ color: "var(--cd-gray-500)" }} />
            <span
              style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".1em",
                textTransform: "uppercase", color: "var(--cd-gray-700)",
              }}
            >
              Share {domain}
            </span>
          </div>
          {targets.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={t.run}
              data-testid={`share-${t.key}-btn`}
              aria-label={`Share on ${t.label}`}
              title={t.label}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "9px 15px", borderRadius: 999,
                border: "1px solid var(--cd-gray-200)", background: "white",
                color: t.key === "copy" && copied ? "#047857" : "var(--cd-navy-950)",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                transition: "border-color 180ms ease, transform 180ms ease, background-color 180ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = accent;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--cd-gray-200)";
                e.currentTarget.style.transform = "";
              }}
            >
              {t.icon}
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {offer?.eligible && shared && !code && (
          <form
            onSubmit={claim}
            data-testid="share-nudge-form"
            style={{
              marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--cd-gray-200)",
              display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--cd-navy-950)", marginRight: "auto" }}>
              Thanks for sharing — where should we send your {rewardLabel} code?
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="share-nudge-email"
              style={{
                flex: "1 1 240px", minWidth: 200, padding: "10px 14px",
                border: "1px solid var(--cd-gray-200)", borderRadius: 999,
                fontSize: 14, fontFamily: "inherit", outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={claiming}
              data-testid="share-nudge-claim-btn"
              style={{
                padding: "10px 20px", borderRadius: 999, border: 0,
                background: "var(--cd-navy-950)", color: "white",
                fontSize: 14, fontWeight: 700, cursor: claiming ? "wait" : "pointer",
              }}
            >
              {claiming ? "Sending…" : `Get ${rewardLabel}`}
            </button>
            {error && (
              <div data-testid="share-nudge-error" style={{ flexBasis: "100%", fontSize: 13, color: "#B42318" }}>
                {error}
              </div>
            )}
          </form>
        )}

        {code && (
          <div
            data-testid="share-nudge-code"
            style={{
              marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--cd-gray-200)",
              display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
            }}
          >
            <span style={{ fontSize: 14, color: "var(--cd-navy-950)", fontWeight: 600, marginRight: "auto" }}>
              {sym}{Number(code.amount).toLocaleString()} off is yours — new total{" "}
              <strong>{sym}{Number(code.new_total).toLocaleString()}</strong>. Emailed to you, valid until{" "}
              {String(code.expires_at).slice(0, 10)}.
            </span>
            <button
              type="button"
              onClick={copyCode}
              data-testid="share-nudge-copy-code"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 18px", borderRadius: 999,
                border: `1px dashed ${accent}`, background: `${accent}14`,
                color: "var(--cd-navy-950)", fontSize: 15, fontWeight: 800,
                letterSpacing: ".06em", cursor: "pointer", fontFamily: "monospace",
              }}
            >
              {code.code}
              {codeCopied ? <Check size={14} strokeWidth={2.4} /> : <Copy size={14} strokeWidth={1.8} />}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.9 2H22l-6.8 7.8L23 22h-6.3l-4.9-6.4L5.9 22H2.8l7.3-8.3L1.5 2h6.4l4.6 6.1L18.9 2Zm-1.1 18h1.7L7.4 3.8H5.6L17.8 20Z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.35c0-1.28-.02-2.92-1.78-2.92-1.78 0-2.05 1.39-2.05 2.83V21h-4V9Z" />
  </svg>
);

export default ShareRow;
